import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Popover,
} from '@mui/material';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import SecurityIcon from '@mui/icons-material/Security';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelectMethod: (method: 'TOTP' | 'SMS') => void;
  anchorEl?: HTMLElement | null;
  defaultMethod?: 'TOTP' | 'SMS'; // Default method to highlight (not used for styling)
  canDisableCompanyMfa?: boolean; // Show option to disable company MFA (for admin/owner)
  onDisableCompanyMfa?: () => void; // Handler to disable company MFA
}

const MfaMethodSelectionDialog: React.FC<Props> = ({ open, onClose, onSelectMethod, anchorEl, defaultMethod = 'TOTP', canDisableCompanyMfa = false, onDisableCompanyMfa }) => {
  const [selectedMethod, setSelectedMethod] = React.useState<'TOTP' | 'SMS' | null>(null);
  const { t } = useTranslation();

  // Reset selection when dialog closes
  React.useEffect(() => {
    if (!open) {
      setSelectedMethod(null);
    }
  }, [open]);

  const methodOptions = (
    <>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontSize: '12px' }}>
        {t('sentences:pages.mfa_setup.choose_method_description') || 
          'Select how you want to receive your verification code.'}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Authenticator App Option */}
        <Button
          variant="outlined"
          fullWidth
          onClick={() => {
            setSelectedMethod('TOTP');
            onSelectMethod('TOTP');
          }}
          sx={{
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            borderColor: selectedMethod === 'TOTP' ? '#1976d2' : '#1976d2',
            backgroundColor: selectedMethod === 'TOTP' ? '#1976d2' : 'transparent',
            color: selectedMethod === 'TOTP' ? '#fff' : '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: selectedMethod === 'TOTP' ? '#1565c0' : '#e3f2fd',
            },
          }}
        >
          <SecurityIcon sx={{ fontSize: '32px', color: selectedMethod === 'TOTP' ? '#fff' : '#1976d2' }} />
          <Typography variant="h6" sx={{ fontWeight: 500, color: selectedMethod === 'TOTP' ? '#fff' : '#1976d2', fontSize: '14px', textTransform: 'uppercase' }}>
            {t('sentences:pages.mfa_setup.method_totp') || 'Authenticator App'}
          </Typography>
          <Typography variant="body2" sx={{ color: selectedMethod === 'TOTP' ? '#fff' : 'text.secondary', textAlign: 'center', fontSize: '11px', textTransform: 'none' }}>
            {t('sentences:pages.mfa_setup.method_totp_description') || 
              'Use an authenticator app like Google Authenticator, Microsoft Authenticator or Authy.'}
          </Typography>
        </Button>

        {/* SMS Option */}
        <Button
          variant="outlined"
          fullWidth
          onClick={() => {
            setSelectedMethod('SMS');
            onSelectMethod('SMS');
          }}
          sx={{
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            borderColor: '#1976d2',
            backgroundColor: selectedMethod === 'SMS' ? '#1976d2' : 'transparent',
            color: selectedMethod === 'SMS' ? '#fff' : '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: selectedMethod === 'SMS' ? '#1565c0' : '#e3f2fd',
            },
          }}
        >
          <SmartphoneOutlinedIcon sx={{ fontSize: '32px', color: selectedMethod === 'SMS' ? '#fff' : '#1976d2' }} />
          <Typography variant="h6" sx={{ fontWeight: 500, color: selectedMethod === 'SMS' ? '#fff' : '#1976d2', fontSize: '14px', textTransform: 'uppercase' }}>
            {t('sentences:pages.mfa_setup.method_sms') || 'SMS (Text message)'}
          </Typography>
          <Typography variant="body2" sx={{ color: selectedMethod === 'SMS' ? '#fff' : 'text.secondary', textAlign: 'center', fontSize: '11px', textTransform: 'none' }}>
            {t('sentences:pages.mfa_setup.method_sms_description') || 
              'Requires a phone number.'}
          </Typography>
        </Button>
      </Box>
    </>
  );

  // Use Popover if anchorEl is provided, otherwise use Dialog (centered)
  if (anchorEl) {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={canDisableCompanyMfa ? onClose : undefined}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: '400px',
            maxWidth: '90vw',
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '16px' }}>
            {t('sentences:pages.mfa_setup.choose_method_title') || 'Two-Factor Authentication'}
          </Typography>
          {methodOptions}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            {canDisableCompanyMfa && (
              <Button onClick={onClose} size="small" sx={{ textTransform: 'uppercase' }}>
                {t('verbs.cancel')}
              </Button>
            )}
          </Box>
        </Box>
      </Popover>
    );
  }

  // Fallback to centered Dialog if no anchor
  // If canDisableCompanyMfa is false, prevent closing (regular users must set up MFA)
  return (
    <Dialog 
      open={open} 
      onClose={canDisableCompanyMfa ? onClose : undefined} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={!canDisableCompanyMfa}
    >
      <DialogTitle>
        {t('sentences:pages.mfa_setup.choose_method_title') || 'Two-Factor Authentication'}
      </DialogTitle>
      <DialogContent>
        {methodOptions}
        {canDisableCompanyMfa && onDisableCompanyMfa && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('sentences:pages.mfa_setup.disable_company_mfa_description') || 'As an admin or owner, you can disable MFA for your company instead.'}
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={onDisableCompanyMfa}
              sx={{
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  borderColor: '#b71c1c',
                  backgroundColor: '#ffebee',
                },
              }}
            >
              {t('sentences:pages.mfa_setup.disable_company_mfa_button') || 'Disable Company MFA'}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {canDisableCompanyMfa && (
          <Button onClick={onClose}>
            {t('verbs.cancel')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MfaMethodSelectionDialog;
